import { SprdResponsiveImageData } from '@/components/ResponsiveImage/SprdResponsiveImage';
import {
  getCompositionImageSrc,
  getProductImageSrc,
  getProductTypeImageSrc,
} from '@/utility/images';

export type ProductTypeImage = {
  type: 'productType';
  productTypeId: string;
  viewId: string;
  appearanceId: string;
  modelId?: string;
  crop?: Crop;
  backgroundColor?: string;
};

export type ProductImage = {
  type: 'product';
  vpString: string;
  viewId: string;
  appearanceId: string;
  modelId?: string;
  crop?: Crop;
  backgroundColor?: string;
  badges?: {
    label: string;
    position: 'bottom-left' | 'top-left' | 'top-right';
    desktopPosition?: 'bottom-left' | 'top-left' | 'top-right';
  }[];
};

export type CompositionImage = {
  type: 'composition';
  vpString: string;
  viewId: string;
  appearanceId: string;
  backgroundColor?: string;
};

export type SprdResponsiveDesktopImageWidth =
  | 50
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 1000
  | 1100
  | 1200;
export const SPRD_RESPONSIVE_DESKTOP_IMAGE_WIDTHS = [200, 300, 400, 500, 600, 700, 800].sort(
  (a, b) => a - b
) as SprdResponsiveDesktopImageWidth[];

export type SprdResponsiveMobileImageWidth = 150 | 300 | 450 | 600 | 750 | 900;
export const SPRD_RESPONSIVE_MOBILE_IMAGE_WIDTHS = [150, 300, 450, 600].sort(
  (a, b) => a - b
) as SprdResponsiveMobileImageWidth[];

export const DEFAULT_WIDTHS = {
  desktop: SPRD_RESPONSIVE_DESKTOP_IMAGE_WIDTHS,
  mobile: SPRD_RESPONSIVE_MOBILE_IMAGE_WIDTHS,
};

export const LIST_PAGE_IMAGE_SIZES = '(min-width: 1024px) 18vw, (min-width: 1920px) 350px, 45vw';

export const getCardImageSrc = (
  baseImageUrl: string,
  image: ProductTypeImage | ProductImage | CompositionImage,
  width?: number,
  height?: number,
  mediaType?: 'webp' | 'jpg' | undefined
): string => {
  switch (image.type) {
    case 'productType': {
      return getProductTypeImageSrc(
        baseImageUrl,
        image.productTypeId,
        image.viewId,
        image.appearanceId,
        image.modelId,
        image.crop,
        width,
        height,
        image.backgroundColor,
        mediaType
      );
    }
    case 'product': {
      return getProductImageSrc(
        baseImageUrl,
        image.vpString,
        image.viewId,
        image.appearanceId,
        image.modelId,
        image.crop,
        width,
        height,
        image.backgroundColor
      );
    }
    case 'composition': {
      return getCompositionImageSrc(
        baseImageUrl,
        image.vpString,
        image.viewId,
        image.appearanceId,
        width,
        height,
        image.backgroundColor
      );
    }
  }
};

/** We have to generate the old image urls so that Google will recognize the images. In the browser this image should be never rendered / visible, the src is just in the code. */
export const getOldCustomerJourneyImageSrc = (
  imageBaseUrl: string,
  image: SprdResponsiveImageData,
  width: number,
  height: number
): string => {
  return getCardImageSrc(
    imageBaseUrl,
    { ...image, backgroundColor: image.backgroundColor ?? '#F2F2F2' },
    width,
    height,
    'jpg'
  );
};

export const getImage = (src: string, width: number, height: number): string => {
  const url = new URL(src);
  url.searchParams.set('width', width.toString());
  url.searchParams.set('height', height.toString());
  return url.toString();
};

const getMobileImageHeight = (width: SprdResponsiveMobileImageWidth): number =>
  Math.round((width / 3) * 4);
export const getMobileImage = (src: string, width: SprdResponsiveMobileImageWidth): string => {
  const height = getMobileImageHeight(width);
  return getImage(src, width, height);
};

const getDesktopImageHeight = (width: SprdResponsiveDesktopImageWidth): number =>
  Math.round((width / 2) * 3);
export const getDesktopImage = (src: string, width: SprdResponsiveDesktopImageWidth): string => {
  const height = getDesktopImageHeight(width);
  return getImage(src, width, height);
};
